import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * Rounds a value to the nearest tenth, avoiding floating point drift
 * (e.g. `0.1 + 0.2` in JS).
 *
 * @param {number} value
 * @returns {number}
 */
function roundToTenth(value) {
  return Math.round(value * 10) / 10;
}

/**
 * Rounds a value DOWN to the nearest tenth. Used specifically for quantity->meter: nearest-
 * rounding could round up past the quantity's true safe boundary (e.g. plants_per_meter=6,
 * quantity=1 -> round(1/6, 1) = 0.2, but ceil(0.2 * 6) = 2, not 1 - that displayed meter value
 * never actually round-trips back to the quantity it was derived from). Flooring always stays
 * inside the range that ceils back to the same quantity, so the pair never contradicts itself.
 *
 * @param {number} value
 * @returns {number}
 */
function floorToTenth(value) {
  return Math.floor(value * 10) / 10;
}

/**
 * The "Meter" field, a sibling of the "Stuks" quantity-selector-component inside
 * .bbd-buy-buttons__quantity-row. Keeps the two in sync: changing the meter recalculates
 * the quantity (rounded up, so the customer always gets enough plants), and changing the
 * quantity recalculates the meter (rounded down, so it never claims to cover more length
 * than that quantity actually guarantees).
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} meterInput
 * @property {HTMLButtonElement} meterMinusButton
 * @property {HTMLButtonElement} meterPlusButton
 *
 * @extends {Component<Refs>}
 */
export class MeterQuantityCalculatorComponent extends Component {
  requiredRefs = ['meterInput', 'meterMinusButton', 'meterPlusButton'];

  connectedCallback() {
    super.connectedCallback();
    // The quantity-selector-component is a sibling, not a descendant, so its bubbled
    // update event never reaches a listener on `this` - listen on the shared row ancestor
    // instead, which is on the bubbling path from both siblings.
    this.#row?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    // Re-derive the hidden state from the data attribute rather than trusting the
    // server-rendered `hidden` attribute alone, so the two can never drift apart.
    this.#applyPlantsPerMeter(this.#plantsPerMeter);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#row?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  get #row() {
    return this.closest('.bbd-buy-buttons__quantity-row');
  }

  /**
   * Called from product-form.js after a variant switch, with the new variant's
   * `custom.plants_per_meter` metafield value (or an empty/null value if unset).
   *
   * @param {string | number | null | undefined} value
   */
  updatePlantsPerMeter(value) {
    this.#applyPlantsPerMeter(parseIntOrDefault(value, 0));
  }

  /**
   * @param {number} plantsPerMeter
   */
  #applyPlantsPerMeter(plantsPerMeter) {
    this.dataset.plantsPerMeter = plantsPerMeter.toString();
    this.hidden = plantsPerMeter <= 0;

    if (plantsPerMeter > 0) {
      this.#syncMeterFromQuantity();
    }
  }

  /**
   * @returns {import('@theme/component-quantity-selector').QuantitySelectorComponent}
   */
  get #quantitySelector() {
    const quantitySelector = this.#row?.querySelector('quantity-selector-component, cart-quantity-selector-component');

    if (!quantitySelector) {
      throw new Error('Missing sibling <quantity-selector-component /> inside the same .bbd-buy-buttons__quantity-row');
    }

    // Close the same custom-element upgrade race documented in @theme/component: when a whole
    // subtree is inserted after both elements are already defined (e.g. a variant-switch morph),
    // this component's connectedCallback can run before its sibling's. upgrade() is
    // synchronous and a no-op once the element is already upgraded.
    if (typeof quantitySelector.getValue !== 'function') {
      customElements.upgrade(quantitySelector);
    }

    return quantitySelector;
  }

  get #plantsPerMeter() {
    return parseIntOrDefault(this.dataset.plantsPerMeter, 0);
  }

  #handleQuantityUpdate = () => {
    this.#syncMeterFromQuantity();
  };

  #syncMeterFromQuantity() {
    const plantsPerMeter = this.#plantsPerMeter;
    if (plantsPerMeter <= 0) return;

    const quantity = parseIntOrDefault(this.#quantitySelector.getValue(), 0);
    // Math.max(0.1, ...) matches the meter input's own min="0.1" - relevant when
    // plants_per_meter > 10, where flooring 1 plant's worth would otherwise floor to 0.
    this.refs.meterInput.value = Math.max(0.1, floorToTenth(quantity / plantsPerMeter)).toString();
  }

  /**
   * Applies a new meter value: converts it to a quantity (rounded up), lets the
   * quantity-selector-component clamp/snap it against its own min/max/step, then
   * dispatches the update event so add-to-cart, price-per-item, etc. stay in sync.
   *
   * @param {number} meter
   */
  #applyMeter(meter) {
    const plantsPerMeter = this.#plantsPerMeter;
    if (plantsPerMeter <= 0) return;

    const clampedMeter = Math.max(0.1, roundToTenth(meter));
    const quantity = Math.ceil(clampedMeter * plantsPerMeter);
    const quantitySelector = this.#quantitySelector;
    const { quantityInput } = quantitySelector;

    quantitySelector.setValue(quantity.toString());
    quantitySelector.updateConstraints(quantityInput.min, quantityInput.max || null, quantityInput.step);

    const finalQuantity = parseIntOrDefault(quantitySelector.getValue(), quantity);
    const cartLine = quantityInput.dataset.cartLine ? Number(quantityInput.dataset.cartLine) : undefined;

    // Dispatching runs our own #syncMeterFromQuantity synchronously (via the shared row
    // listener), re-deriving the meter field from the final quantity. That's correct when
    // the quantity selector actually clamped the value (stock limits etc.), but plants-per-
    // meter is often small relative to the 0.1 step, so many meter values ceil to the same
    // quantity - left as-is, that resync would silently snap the display straight back to
    // its previous value on every click where the quantity doesn't change, making +/- look
    // completely stuck. Only let the resync stand when clamping actually happened; otherwise
    // show exactly the value the customer clicked/typed to.
    quantitySelector.dispatchEvent(new QuantitySelectorUpdateEvent(finalQuantity, cartLine));

    if (finalQuantity === quantity) {
      this.refs.meterInput.value = clampedMeter.toString();
    }
  }

  /**
   * @param {Event} event
   */
  increaseMeter(event) {
    event.preventDefault();
    this.#applyMeter((parseFloat(this.refs.meterInput.value) || 0) + 0.1);
  }

  /**
   * @param {Event} event
   */
  decreaseMeter(event) {
    event.preventDefault();
    this.#applyMeter((parseFloat(this.refs.meterInput.value) || 0) - 0.1);
  }

  /**
   * @param {Event} event
   */
  setMeter(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    this.#applyMeter(parseFloat(event.target.value) || 0);
  }

  /**
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== event.target) return;

    event.target.select();
  }
}

if (!customElements.get('meter-quantity-calculator-component')) {
  customElements.define('meter-quantity-calculator-component', MeterQuantityCalculatorComponent);
}
